import React from 'react';
import {
  Box,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  MenuItem,
  Tooltip,
  Checkbox,
} from '@mui/material';
import { tableRowTransparentSelectionSx } from '../../../../styles/theme/tableRowOverlays';
import {
  VisibilityOutlined as VisibilityIcon,
  EditOutlined as EditIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
  ChevronLeft as PrevPageIcon,
  ChevronRight as NextPageIcon,
  LockOutlined as LockIcon,
  LockOpenOutlined as LockOpenIcon,
  ReceiptOutlined as ReceiptIcon,
} from '@mui/icons-material';
import { formatDateTime } from '../../../../utils/formatDate';
import {
  formatPieceSalePrice,
  pieceSalePriceTableCellSx,
} from '../../../../utils/formatters/money';
import { formatDecimal } from '../../../../utils/localeNumber';
import { getTransferDisplayAtIso } from '../../../../utils/inventory-transfers/transferDisplayDate';
import {
  InventoryItem,
  PaginationInfo,
  type WarehouseInventorySortField,
} from '../../../../types/inventory';
import { InventorySortableHeadCell } from '../InventorySortableHeadCell';
import { useInventoryQueryTable } from '../InventoryQueryTable.hooks';
import EmptyStateMessage from '../../../common/EmptyStateMessage';
import {
  TableContainerStyled,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeadCell,
  StatusChip,
  PaginationContainer,
  PaginationInfo as PaginationInfoStyled,
  PaginationText,
  PaginationControls,
  StyledFormControl,
  StyledSelect,
  PageButton,
  EllipsisButton,
} from '../InventoryQueryTable.styles';
import { getVisiblePaginationItems, PaginationGoToPageField } from '../../../common/Pagination';
import { ActionButton } from '../../../common/Table/Table.styles';
import { mapInventoryAvailability, mapWarehouseStatus } from '../../../common/status';
import { calendarDaysSinceRegistration } from '../../../../utils/inventory/calendarDaysSinceTransfer';
import { useInventoryBranchPermissions } from '../../../../hooks/useInventoryBranchPermissions';
import { useInventoryGeneralRequestPermissions } from '../../../../hooks/useInventoryRequestPermissions';
import { usePermission } from '../../../../hooks/usePermission';
import { INVENTORY_PERMISSIONS } from '../../../../constants/permissions.constants';
import {
  INV_AVAILABILITY_BLOCKED_LABEL,
  isAvailabilityWrittenOff,
} from '../../../../constants/inventory-status-labels';

export interface BranchTableProps {
  items: InventoryItem[];
  onViewDetails: (id: string) => void;
  pagination: PaginationInfo;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  selectedItems: string[];
  onSelectItems: (items: string[]) => void;
  onEditItem?: (id: string) => void;
  onReserveItem?: (id: string) => void;
  onTransferSelected?: (selectedItems: string[]) => void;
  hidePagination?: boolean;
  onPrintLabel?: (item: InventoryItem) => void;
  /**
   * Si es true, no se muestran Nº Registro, Fecha Transfer. ni Cód. Transferencia (el JSX se mantiene para reactivarlas).
   * Por defecto true en Gestión en Sucursal.
   */
  hideRecordAndTransferColumns?: boolean;
  sortBy: WarehouseInventorySortField;
  sortOrder: 'asc' | 'desc';
  onSortChange: (field: WarehouseInventorySortField) => void;
  /** Inventario general: columnas con ubicación, peso, días en inventario y dos estados. */
  variant?: 'branch' | 'general';
  /** Inventario general: habilita checkboxes para crear solicitud. */
  enableRequestSelection?: boolean;
  /** Origen bloqueado tras la primera pieza seleccionada (nombre de ubicación). */
  lockedOriginLocationName?: string | null;
}

const BranchTable: React.FC<BranchTableProps> = ({
  items,
  onViewDetails,
  pagination,
  onPageChange,
  onItemsPerPageChange,
  selectedItems,
  onSelectItems,
  onEditItem,
  onReserveItem,
  onTransferSelected,
  hidePagination = false,
  onPrintLabel,
  hideRecordAndTransferColumns = true,
  sortBy,
  sortOrder,
  onSortChange,
  variant = 'branch',
  enableRequestSelection = false,
  lockedOriginLocationName = null,
}) => {
  const isGeneral = variant === 'general';
  // Branch inventory permissions - centralized in a single hook
  const branchPerms = useInventoryBranchPermissions();
  const { canCreateRequest } = useInventoryGeneralRequestPermissions();
  const canViewInventoryGeneral = usePermission(INVENTORY_PERMISSIONS.GENERAL.VIEW);

  const showRequestCheckboxes = isGeneral && enableRequestSelection && canCreateRequest;
  const showTransferCheckboxes =
    !isGeneral && Boolean(onTransferSelected) && branchPerms.canTransfer;
  const showCheckboxes = showRequestCheckboxes || showTransferCheckboxes;

  const {
    selectableItems,
    isAllSelected,
    handleItemsPerPageChange,
    paginationInfo,
    handleSelectItem,
    handleSelectAll,
    getCheckboxStyles,
    getItemSelectionState,
  } = useInventoryQueryTable({
    items,
    pagination,
    selectedItems,
    onSelectItems,
    onItemsPerPageChange,
    isInventoryBranchQuery: !isGeneral && !showRequestCheckboxes,
    isInventoryGeneralRequest: showRequestCheckboxes,
    lockedOriginLocationName,
    isAdmin: true,
  });

  const isIndeterminate =
    selectedItems.length > 0 &&
    !isAllSelected &&
    selectableItems.some((item) => selectedItems.includes(item.id));

  const formatLengthCm = (value: number | undefined) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const cmValue = Number(value) / 10;
    return formatDecimal(cmValue, 2, { trimTrailingZeros: true }) || '—';
  };
  const formatWeightGrams = (value: number | undefined) => {
    if (value == null || Number.isNaN(Number(value))) return '—';
    const g = Number(value);
    if (g >= 1000) {
      const kg = g / 1000;
      return `${formatDecimal(kg, 2, { trimTrailingZeros: true })} kg`;
    }
    return `${formatDecimal(g, 2, { trimTrailingZeros: true })} g`;
  };
  const formatDaysInStock = (item: InventoryItem) => {
    const n = calendarDaysSinceRegistration(item);
    if (n === null) return '—';
    return String(n);
  };
  const displayText = (value: string | undefined | null) => {
    const t = value?.trim();
    return t ? t : '—';
  };
  const formatIngresoAt = (item: InventoryItem) => {
    const iso = getTransferDisplayAtIso(item.transfer, item.firstAssignedAt);
    const formatted = formatDateTime(iso);
    return formatted || '—';
  };

  // branch (Gestión en Sucursal): 11 columnas de datos + Acción (+ checkbox opcional)
  const totalColumns = isGeneral
    ? (showCheckboxes ? 1 : 0) + 13
    : (showTransferCheckboxes ? 1 : 0) + 12 + (hideRecordAndTransferColumns ? 0 : 3);

  if (!branchPerms.canView && !canViewInventoryGeneral) return null;

  return (
    <TableContainerStyled>
      <StyledTableContainer>
        <StyledTable>
          <StyledTableHead>
            <TableRow>
              {showCheckboxes && (
                <StyledTableHeadCell padding="checkbox">
                  <Tooltip
                    title={
                      selectableItems.length === 0
                        ? 'No hay piezas seleccionables en esta página'
                        : isAllSelected
                          ? 'Deseleccionar todas las piezas de esta página'
                          : 'Seleccionar todas las piezas seleccionables de esta página'
                    }
                    placement="top"
                    arrow
                  >
                    <Box>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected && selectableItems.length > 0}
                        onChange={handleSelectAll}
                        size="small"
                        disabled={selectableItems.length === 0}
                        sx={getCheckboxStyles(selectableItems.length > 0)}
                      />
                    </Box>
                  </Tooltip>
                </StyledTableHeadCell>
              )}
              {isGeneral ? (
                <>
                  <InventorySortableHeadCell
                    label="Código"
                    field="productCodeUnique"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <InventorySortableHeadCell
                    label="Nombre"
                    field="productName"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <InventorySortableHeadCell
                    label="Ubicación"
                    field="locationName"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <StyledTableHeadCell sx={{ minWidth: 96 }}>Talla</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 96 }}>Color</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 96 }}>Largo (cm)</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 88 }}>Peso</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 112 }}>Cód. Proveedor</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 108 }}>
                    Días en inventario
                  </StyledTableHeadCell>
                  <InventorySortableHeadCell
                    label="Precio de venta"
                    field="salePrice"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <InventorySortableHeadCell
                    label="Estado Inventario"
                    field="status"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <InventorySortableHeadCell
                    label="Estado venta"
                    field="availabilityStatus"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <StyledTableHeadCell>Acción</StyledTableHeadCell>
                </>
              ) : (
                <>
                  <InventorySortableHeadCell
                    label="Código"
                    field="productCodeUnique"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <InventorySortableHeadCell
                    label="Nombre"
                    field="productName"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <InventorySortableHeadCell
                    label="Tipo"
                    field="categoryName"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <StyledTableHeadCell sx={{ minWidth: 96 }}>Talla</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 96 }}>Color</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 96 }}>Largo (cm)</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 88 }}>Peso</StyledTableHeadCell>
                  <StyledTableHeadCell sx={{ minWidth: 80 }}>Cód. Proveedor</StyledTableHeadCell>
                  <InventorySortableHeadCell
                    label="Precio de venta"
                    field="salePrice"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <InventorySortableHeadCell
                    label="Estado de Venta"
                    field="availabilityStatus"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  <InventorySortableHeadCell
                    label="Fecha de ingreso"
                    field="firstAssignedAt"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={onSortChange}
                  />
                  {!hideRecordAndTransferColumns && (
                    <>
                      <InventorySortableHeadCell
                        label="Nº Registro"
                        field="recordCode"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortChange={onSortChange}
                      />
                      <InventorySortableHeadCell
                        label="Fecha Transfer."
                        field="firstAssignedAt"
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSortChange={onSortChange}
                      />
                      <StyledTableHeadCell>Cód. Transferencia</StyledTableHeadCell>
                    </>
                  )}
                  <StyledTableHeadCell>Acción</StyledTableHeadCell>
                </>
              )}
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {items.length > 0 ? (
              items.map((item) => (
                <StyledTableRow
                  key={item.id}
                  hover
                  selected={false}
                  sx={tableRowTransparentSelectionSx}
                >
                  {showCheckboxes &&
                    (() => {
                      const { selectable, reason } = getItemSelectionState(item);
                      return (
                        <StyledTableCell padding="checkbox">
                          <Tooltip title={reason} placement="top" arrow>
                            <Box sx={{ position: 'relative' }}>
                              <Checkbox
                                checked={selectedItems.includes(item.id)}
                                onChange={() => handleSelectItem(item.id)}
                                disabled={!selectable}
                                size="small"
                                sx={getCheckboxStyles(selectable)}
                              />
                            </Box>
                          </Tooltip>
                        </StyledTableCell>
                      );
                    })()}
                  {isGeneral ? (
                    <>
                      <StyledTableCell tone="muted">{item.id}</StyledTableCell>
                      <StyledTableCell tone="muted">{item.name}</StyledTableCell>
                      <StyledTableCell tone="muted">{item.locationName || '—'}</StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 96 }}>
                        {displayText(item.diameterSize)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 96 }}>
                        {displayText(item.color)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 96 }}>
                        {formatLengthCm(item.lengthMm)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 88 }}>
                        {formatWeightGrams(item.weightGrams)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 112 }}>
                        {displayText(item.workshopCode)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 108 }}>
                        {formatDaysInStock(item)}
                      </StyledTableCell>
                      <StyledTableCell
                        tone="muted"
                        align="right"
                        sx={{ minWidth: 108, ...pieceSalePriceTableCellSx }}
                      >
                        {formatPieceSalePrice(item.price)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {(() => {
                          const chip = mapWarehouseStatus(item.status);
                          return <StatusChip label={chip.label} size="small" color={chip.color} />;
                        })()}
                      </StyledTableCell>
                      <StyledTableCell>
                        {(() => {
                          const chip = mapInventoryAvailability(item.availabilityStatus);
                          return <StatusChip label={chip.label} size="small" color={chip.color} />;
                        })()}
                      </StyledTableCell>
                    </>
                  ) : (
                    <>
                      <StyledTableCell tone="muted">{item.id}</StyledTableCell>
                      <StyledTableCell tone="muted">{item.name}</StyledTableCell>
                      <StyledTableCell tone="muted">{item.type}</StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 96 }}>
                        {displayText(item.diameterSize)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 96 }}>
                        {displayText(item.color)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 96 }}>
                        {formatLengthCm(item.lengthMm)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 88 }}>
                        {formatWeightGrams(item.weightGrams)}
                      </StyledTableCell>
                      <StyledTableCell tone="muted" sx={{ minWidth: 80 }}>
                        {displayText(item.workshopCode)}
                      </StyledTableCell>
                      <StyledTableCell
                        tone="muted"
                        align="right"
                        sx={{ minWidth: 108, ...pieceSalePriceTableCellSx }}
                      >
                        {formatPieceSalePrice(item.price)}
                      </StyledTableCell>
                      <StyledTableCell>
                        {(() => {
                          const chip = mapInventoryAvailability(item.availabilityStatus);
                          return <StatusChip label={chip.label} size="small" color={chip.color} />;
                        })()}
                      </StyledTableCell>
                      <StyledTableCell tone="muted">{formatIngresoAt(item)}</StyledTableCell>
                      {!hideRecordAndTransferColumns && (
                        <>
                          <StyledTableCell tone="muted">{item.recordCode ?? '—'}</StyledTableCell>
                          <StyledTableCell tone="muted">{formatIngresoAt(item)}</StyledTableCell>
                          <StyledTableCell tone="muted">
                            {item.transfer?.transferCode || '-'}
                          </StyledTableCell>
                        </>
                      )}
                    </>
                  )}
                  <StyledTableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip title="Ver detalles" placement="top" arrow>
                        <ActionButton size="small" onClick={() => onViewDetails(item.id)}>
                          <VisibilityIcon fontSize="small" />
                        </ActionButton>
                      </Tooltip>
                      {onEditItem &&
                        branchPerms.canEdit &&
                        !isAvailabilityWrittenOff(item.availabilityStatus) && (
                          <Tooltip title="Editar pieza" placement="top" arrow>
                            <ActionButton size="small" onClick={() => onEditItem(item.id)}>
                              <EditIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        )}
                      {onReserveItem &&
                        item.availabilityStatus === 'Disponible' &&
                        branchPerms.canReserve && (
                          <Tooltip title="Bloquear pieza" placement="top" arrow>
                            <ActionButton size="small" onClick={() => onReserveItem(item.id)}>
                              <LockIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        )}
                      {onReserveItem &&
                        item.availabilityStatus === INV_AVAILABILITY_BLOCKED_LABEL &&
                        branchPerms.canUnreserve && (
                          <Tooltip title="Liberar bloqueo" placement="top" arrow>
                            <ActionButton
                              size="small"
                              onClick={() => onReserveItem(item.id)}
                              colorVariant="error"
                            >
                              <LockOpenIcon fontSize="small" />
                            </ActionButton>
                          </Tooltip>
                        )}
                      {onPrintLabel && !isAvailabilityWrittenOff(item.availabilityStatus) && (
                        <Tooltip title="Imprimir etiqueta" placement="top" arrow>
                          <ActionButton size="small" onClick={() => onPrintLabel(item)}>
                            <ReceiptIcon fontSize="small" />
                          </ActionButton>
                        </Tooltip>
                      )}
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center">
                  <EmptyStateMessage message="No se encontraron piezas" minHeight={200} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>

      {/* Pagination */}
      {!hidePagination && (
        <PaginationContainer>
          <PaginationInfoStyled>
            <PaginationText>
              Mostrando {paginationInfo.startItem}-{paginationInfo.endItem} de{' '}
              {pagination.totalItems} elementos
            </PaginationText>
          </PaginationInfoStyled>

          <PaginationControls
            sx={{
              flex: 1,
              minWidth: 0,
              flexWrap: 'wrap',
            }}
          >
            <StyledFormControl size="small">
              <StyledSelect value={pagination.itemsPerPage} onChange={handleItemsPerPageChange}>
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </StyledSelect>
            </StyledFormControl>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                flex: 1,
                minWidth: 0,
                flexWrap: 'wrap',
              }}
            >
              <PageButton onClick={() => onPageChange(1)} disabled={pagination.currentPage === 1}>
                <FirstPageIcon />
              </PageButton>
              <PageButton
                onClick={() => onPageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
              >
                <PrevPageIcon />
              </PageButton>
              {getVisiblePaginationItems(pagination.totalPages, pagination.currentPage).map(
                (page, index) => (
                  <Box key={index}>
                    {page === '...' ? (
                      <EllipsisButton disabled>
                        <Typography variant="body2">...</Typography>
                      </EllipsisButton>
                    ) : (
                      <PageButton
                        onClick={() => onPageChange(page as number)}
                        sx={{
                          ...(page === pagination.currentPage && {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          }),
                        }}
                      >
                        {page}
                      </PageButton>
                    )}
                  </Box>
                ),
              )}

              <PaginationGoToPageField
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                onCommit={onPageChange}
              />
              <PageButton
                onClick={() => onPageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <NextPageIcon />
              </PageButton>
              <PageButton
                onClick={() => onPageChange(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
              >
                <LastPageIcon />
              </PageButton>
            </Box>
          </PaginationControls>
        </PaginationContainer>
      )}
    </TableContainerStyled>
  );
};

export default BranchTable;
